import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "sonner";
import { useTranslation } from "react-i18next";
import { initAnalytics, trackPageView } from "@/utils/analytics";
import "@/i18n/config";
import { getLangFromPath } from "@/i18n/useLocalizedPath";

// Main Pages
import HomePage from "@/pages/HomePage";

// Event Pages
import EventsHomePage from "@/pages/events/EventsHomePage";
import ListYourEventPage from "@/pages/events/ListYourEventPage";
import CityEventsPage from "@/pages/events/CityEventsPage";
import VenueEventsPage from "@/pages/events/VenueEventsPage";
import EventDetailPage from "@/pages/events/EventDetailPage";
import EmbedDemoPage from "@/pages/events/EmbedDemoPage";
import SectorEventsPage from "@/pages/events/SectorEventsPage";
import SeriesEventsPage from "@/pages/events/SeriesEventsPage";
import CitySectorEventsPage from "@/pages/events/CitySectorEventsPage";

// Blog Pages
import BlogPage from "@/pages/blog/BlogPage";
import BlogPostPage from "@/pages/blog/BlogPostPage";

// Subscribe Pages
import SubscribePage from "@/pages/subscribe/SubscribePage";

// Embed Pages
import BadgePreviewPage from "@/pages/embed/BadgePreviewPage";

// Admin Pages
import AdminLoginPage from "@/pages/admin/AdminLoginPage";
import AdminEventsListPage from "@/pages/admin/AdminEventsListPage";
import AdminEventCreatePage from "@/pages/admin/AdminEventCreatePage";
import AdminEventEditPage from "@/pages/admin/AdminEventEditPage";
import AdminChangeRequestsListPage from "@/pages/admin/AdminChangeRequestsListPage";
import AdminChangeRequestReviewPage from "@/pages/admin/AdminChangeRequestReviewPage";
import AdminAnalyticsPage from "@/pages/admin/AdminAnalyticsPage";
import AdminBlogsListPage from "@/pages/admin/AdminBlogsListPage";
import AdminBlogEditPage from "@/pages/admin/AdminBlogEditPage";
import AdminEmbedsPage from "@/pages/admin/AdminEmbedsPage";
import AdminReportsPage from "@/pages/admin/AdminReportsPage";
import AdminReportEditPage from "@/pages/admin/AdminReportEditPage";
import AdminSettingsPage from "@/pages/admin/AdminSettingsPage";
import AdminPartnerKeysPage from "@/pages/admin/AdminPartnerKeysPage";
import AdminTrendingPage from "@/pages/admin/AdminTrendingPage";
import AdminBoostPage from "@/pages/admin/AdminBoostPage";
import AdminSubmissionsPage from "@/pages/admin/AdminSubmissionsPage";
import AdminDateVerificationPage from "@/pages/admin/AdminDateVerificationPage";
import AdminTranslationsPage from "@/pages/admin/AdminTranslationsPage";
import AdminWebsiteReviewPage from "@/pages/admin/AdminWebsiteReviewPage";
import ProtectedRoute from "@/components/admin/ProtectedRoute";

// Organiser Pages
import OrganiserManageEventPage from "@/pages/organiser/OrganiserManageEventPage";

// Insights Pages
import InsightsPage from "@/pages/insights/InsightsPage";
import InsightReportPage from "@/pages/insights/InsightReportPage";
import CompliancePage from "@/pages/public/CompliancePage";
import AboutListingsPage from "@/pages/public/AboutListingsPage";

const TERMS_OF_SERVICE_CANONICAL_URL = "https://jigsawconferences.co.uk/terms-of-service";

function TermsOfServiceRedirect() {
  React.useEffect(() => {
    window.location.replace(TERMS_OF_SERVICE_CANONICAL_URL);
  }, []);
  return null;
}
import NotFoundPage from "@/pages/NotFoundPage";

// Legal Pages
import PrivacyPolicyPage from "@/pages/legal/PrivacyPolicyPage";
import CookiePolicyPage from "@/pages/legal/CookiePolicyPage";

// Boost Pages
import BoostDashboardPage from "@/pages/boost/BoostDashboardPage";

// Genome Intelligence Pages
import EventComparePage from "@/pages/genome/EventComparePage";
import TrendsPage from "@/pages/genome/TrendsPage";

// SEO Autopilot Pages
import AutopilotPage from "@/pages/autopilot/AutopilotPage";
import AutopilotSitemap from "@/pages/autopilot/AutopilotSitemap";

// Analytics page view tracker component
function AnalyticsTracker() {
  const location = useLocation();
  
  useEffect(() => {
    // Track page view on route change
    trackPageView(location.pathname, document.title);
  }, [location]);
  
  return null;
}

// Scroll to top on route change
function ScrollToTop() {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  
  return null;
}

// Keeps i18next language in sync with the URL language prefix (/de, /fr, /es)
function LangSync() {
  const { pathname } = useLocation();
  const { i18n } = useTranslation();

  useEffect(() => {
    const lang = getLangFromPath(pathname);
    if (i18n.language !== lang) {
      i18n.changeLanguage(lang);
    }
    document.documentElement.lang = lang;
  }, [pathname, i18n]);

  return null;
}

// Public routes are rendered for the default language (no prefix) and for each
// supported language prefix (/de, /fr, /es). Define them once here.
const PUBLIC_ROUTES = [
  { path: "", element: <HomePage /> },
  { path: "events", element: <EventsHomePage /> },
  { path: "events/list-your-event", element: <ListYourEventPage /> },
  { path: "events/embed", element: <EmbedDemoPage /> },
  { path: "events/sector/:tagSlug", element: <SectorEventsPage /> },
  { path: "events/series/:seriesKey", element: <SeriesEventsPage /> },
  { path: "events/city/:citySlug/sector/:tagSlug", element: <CitySectorEventsPage /> },
  { path: "events/:citySlug", element: <CityEventsPage /> },
  { path: "events/:citySlug/:venueSlug", element: <VenueEventsPage /> },
  { path: "events/:citySlug/:venueSlug/:eventSlug", element: <EventDetailPage /> },
  { path: "blog", element: <BlogPage /> },
  { path: "blog/:slug", element: <BlogPostPage /> },
  { path: "subscribe/:filterType/:filterValue", element: <SubscribePage /> },
  { path: "embed/badge-preview", element: <BadgePreviewPage /> },
  { path: "insights", element: <InsightsPage /> },
  { path: "insights/:slug", element: <InsightReportPage /> },
  { path: "compliance", element: <CompliancePage /> },
  { path: "boost/dashboard", element: <BoostDashboardPage /> },
  { path: "compare/:eventSlug1/:eventSlug2", element: <EventComparePage /> },
  { path: "trends", element: <TrendsPage /> },
  { path: "privacy", element: <PrivacyPolicyPage /> },
  { path: "cookies", element: <CookiePolicyPage /> },
  { path: "about-our-listings", element: <AboutListingsPage /> },
  { path: "terms-of-service", element: <TermsOfServiceRedirect /> },
];

const LANG_PREFIXES = ["de", "fr", "es"];

function buildPublicRoutes(prefix) {
  return PUBLIC_ROUTES.map(({ path, element }) => {
    let full;
    if (prefix) {
      full = path ? `/${prefix}/${path}` : `/${prefix}`;
    } else {
      full = path ? `/${path}` : "/";
    }
    return <Route key={full} path={full} element={element} />;
  });
}

function App() {
  // Initialize analytics on app mount
  useEffect(() => {
    initAnalytics();
  }, []);

  return (
    <HelmetProvider>
      <div className="App">
        <Toaster position="top-right" richColors />
        <BrowserRouter>
          <ScrollToTop />
          <LangSync />
          <AnalyticsTracker />
          <Routes>
            {/* Public Routes (default language) */}
            {buildPublicRoutes("")}

            {/* Public Routes (language-prefixed: /de, /fr, /es) */}
            {LANG_PREFIXES.map((prefix) => buildPublicRoutes(prefix))}
            
            {/* Admin Routes */}
            <Route path="/admin" element={<Navigate to="/admin/login" replace />} />
            <Route path="/admin/login" element={<AdminLoginPage />} />
            <Route path="/admin/events" element={<ProtectedRoute><AdminEventsListPage /></ProtectedRoute>} />
            <Route path="/admin/events/new" element={<ProtectedRoute><AdminEventCreatePage /></ProtectedRoute>} />
            <Route path="/admin/events/change-requests" element={<ProtectedRoute><AdminChangeRequestsListPage /></ProtectedRoute>} />
            <Route path="/admin/events/change-requests/:id" element={<ProtectedRoute><AdminChangeRequestReviewPage /></ProtectedRoute>} />
            <Route path="/admin/events/:id" element={<ProtectedRoute><AdminEventEditPage /></ProtectedRoute>} />
            <Route path="/admin/analytics" element={<ProtectedRoute><AdminAnalyticsPage /></ProtectedRoute>} />
            <Route path="/admin/blogs" element={<ProtectedRoute><AdminBlogsListPage /></ProtectedRoute>} />
            <Route path="/admin/blogs/:id" element={<ProtectedRoute><AdminBlogEditPage /></ProtectedRoute>} />
            <Route path="/admin/embeds" element={<ProtectedRoute><AdminEmbedsPage /></ProtectedRoute>} />
            <Route path="/admin/reports" element={<ProtectedRoute><AdminReportsPage /></ProtectedRoute>} />
            <Route path="/admin/reports/:reportId" element={<ProtectedRoute><AdminReportEditPage /></ProtectedRoute>} />
            <Route path="/admin/settings" element={<ProtectedRoute><AdminSettingsPage /></ProtectedRoute>} />
            <Route path="/admin/partner-keys" element={<ProtectedRoute><AdminPartnerKeysPage /></ProtectedRoute>} />
            <Route path="/admin/trending" element={<ProtectedRoute><AdminTrendingPage /></ProtectedRoute>} />
            <Route path="/admin/boost" element={<ProtectedRoute><AdminBoostPage /></ProtectedRoute>} />
            <Route path="/admin/submissions" element={<ProtectedRoute><AdminSubmissionsPage /></ProtectedRoute>} />
            <Route path="/admin/date-verification" element={<ProtectedRoute><AdminDateVerificationPage /></ProtectedRoute>} />
            <Route path="/admin/url-governance" element={<ProtectedRoute><AdminWebsiteReviewPage /></ProtectedRoute>} />
            <Route path="/admin/translations" element={<ProtectedRoute><AdminTranslationsPage /></ProtectedRoute>} />
            
            {/* Non-public portal URLs should not be indexed */}
            <Route path="/owners/*" element={<NotFoundPage />} />
            <Route path="/staff/*" element={<NotFoundPage />} />
            
            {/* Organiser Routes */}
            <Route path="/organiser/events/manage/:eventId/:token" element={<OrganiserManageEventPage />} />

            {/* Catch-all */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </BrowserRouter>
      </div>
    </HelmetProvider>
  );
}

export default App;
